import { Button } from '@/components/ui/button';
import { KeyboardAwareScreenScroll } from '@/components/layout/keyboard-aware-screen-scroll';
import { ScreenHeader } from '@/components/layout/screen-header';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import {
  ActivityIndicator,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { PageTitle } from '@/components/seo/page-title';
import { CONTENT_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import { useEditProfileForm } from './hooks/use-edit-profile-form';
import { useProfilePhotoUpload } from './hooks/use-profile-photo-upload';

export function EditProfileScreen() {
  const form = useEditProfileForm();
  const photo = useProfilePhotoUpload();

  const profileImage = photo.profileImage || form.profileImage;
  const isUploading = photo.isUploading;
  const isSaveDisabled = form.isSaving || isUploading;

  const saveButton = (
    <Button
      variant="primary"
      size="sm"
      onPress={form.handleSave}
      disabled={isSaveDisabled}
      loading={form.isSaving}
      accessibilityRole="button"
      accessibilityLabel="Save profile"
    >
      Save
    </Button>
  );

  if (form.profileLoading || !form.user) {
    return (
      <SafeAreaView className="flex-1" edges={['top']}>
        <ScreenHeader title="Edit Profile" showBackButton rightSlot={saveButton} />
        <View className="flex-1 justify-center items-center">
          <ActivityIndicator size="large" className="text-foreground" />
          <Text className="text-sm mt-2 text-muted-foreground">Loading profile...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <PageTitle title="Edit Profile" />
      <ScreenHeader title="Edit Profile" showBackButton rightSlot={saveButton} />
      <KeyboardAwareScreenScroll
        className="flex-1"
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingVertical: 24,
          ...centeredContent(CONTENT_MAX_WIDTH),
        }}
      >
        <View className="items-center mb-6">
          <View className="relative">
            <View style={{ width: 96, height: 96 }}>
              <UserAvatar
                uri={profileImage}
                name={form.user.name}
                size={96}
                accessibilityLabel="Your profile photo"
                className="border-border bg-surface"
                style={{ borderWidth: 1.5, borderCurve: 'continuous' }}
              />
              {isUploading && (
                <View className="absolute inset-0 w-24 h-24 rounded-full bg-black/50 items-center justify-center">
                  <ActivityIndicator size="small" color="#fff" />
                </View>
              )}
            </View>
            <Button
              variant="primary"
              size="icon"
              onPress={photo.handleChangeProfileImage}
              disabled={isUploading}
              className="absolute bottom-0 right-0 h-11 w-11 rounded-full"
              accessibilityRole="button"
              accessibilityLabel="Change profile photo"
            >
              <Ionicons name="camera" size={20} className="text-primary-foreground" />
            </Button>
          </View>
        </View>

        <TextInput
          label="Name"
          containerClassName="mb-4"
          value={form.name}
          onChangeText={form.setName}
          placeholder="Your full name"
          autoCapitalize="words"
        />

        <View className="mb-4">
          <TextInput
            label="Username"
            value={form.username}
            onChangeText={form.handleUsernameChange}
            placeholder="Your username"
            autoCapitalize="none"
            autoCorrect={false}
            leftIcon={<Text className="text-muted-foreground" style={{ fontSize: 15 }}>@</Text>}
            rightIcon={
              form.checkingUsername ? (
                <ActivityIndicator size="small" className="text-muted-foreground" />
              ) : form.usernameAvailable === true && form.username !== form.originalUsername ? (
                <Text className="text-success text-lg">✓</Text>
              ) : form.usernameAvailable === false ? (
                <Text className="text-error text-lg">✕</Text>
              ) : undefined
            }
            status={
              form.usernameAvailable === false
                ? 'error'
                : form.usernameAvailable === true && form.username !== form.originalUsername
                  ? 'success'
                  : undefined
            }
            inputContainerClassName="bg-card"
          />
          {form.username.length >= 3 && form.username !== form.originalUsername && (
            <Text
              accessibilityLiveRegion="polite"
              className={`text-xs mt-2 ${
                form.checkingUsername
                  ? 'text-muted-foreground opacity-50'
                  : form.usernameAvailable === true
                    ? 'text-success'
                    : form.usernameAvailable === false
                      ? 'text-error'
                      : 'text-muted-foreground'
              }`}
            >
              {form.checkingUsername
                ? 'Checking availability...'
                : form.usernameAvailable === true
                  ? 'Username is available!'
                  : form.usernameAvailable === false
                    ? 'Username is already taken'
                    : ''}
            </Text>
          )}
          {form.username.length > 0 && form.username.length < 3 && (
            <Text className="text-xs mt-2 text-muted-foreground">
              Username must be at least 3 characters long
            </Text>
          )}
        </View>

        <TextInput
          label="Bio"
          containerClassName="mb-4"
          value={form.bio}
          onChangeText={form.setBio}
          placeholder="Tell us about yourself"
          multiline
          numberOfLines={5}
          autoCapitalize="sentences"
        />
      </KeyboardAwareScreenScroll>
    </SafeAreaView>
  );
}
